import { Box, Card, ClickAwayListener, IconButton, Stack, Tooltip } from '@mui/material';
import { DropdownSelectRow } from '@schooly/components/filters';
import { ControlTextField } from '@schooly/components/form-text-field';
import { useFlag } from '@schooly/hooks/use-flag';
import { MoreIcon, TypographyWithOverflowHint } from '@schooly/style';
import { FC, PropsWithChildren, ReactNode } from 'react';

const MAX_LENGTH = 64;

interface FacilityCardProps extends PropsWithChildren {
  index: number;
  placeholder: string;
  endAdornment: ReactNode;
  validateName: (idx: number) => string | true;
}

export const FacilityCard: FC<FacilityCardProps> = ({
  index,
  children,
  placeholder,
  endAdornment,
  validateName,
}) => {
  return (
    <Card sx={{ backgroundColor: 'background.default', border: 'none' }}>
      <Stack gap={1} p={2}>
        <FacilityRow
          index={index}
          placeholder={placeholder}
          endAdornment={endAdornment}
          validateName={validateName}
        />
        {children}
      </Stack>
    </Card>
  );
};

interface FacilityRowProps {
  index: number;
  placeholder: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  validateName: (idx: number) => string | true;
  onBlur?: (idx: number) => void;
}

export const FacilityRow: FC<FacilityRowProps> = ({
  index,
  placeholder,
  startAdornment,
  endAdornment,
  validateName,
  onBlur,
}) => {
  return (
    <Stack direction="row" gap={1}>
      {startAdornment && <Box mt={1.5}>{startAdornment}</Box>}
      <ControlTextField
        name={`facilities.${index}.name`}
        label={placeholder}
        fullWidth
        solid
        inputProps={{ maxLength: MAX_LENGTH }}
        rules={{
          required: true,
          validate: { unique: () => validateName(index) },
          onBlur: () => onBlur?.(index),
        }}
      />
      {endAdornment && <Box mt={1.5}>{endAdornment}</Box>}
    </Stack>
  );
};

export type FacilityAction = {
  label: string;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
  subActions?: FacilityAction[];
};

interface FacilityActionsMenuProps {
  actions: FacilityAction[];
}

export const FacilityActionsMenu: FC<FacilityActionsMenuProps> = ({ actions }) => {
  const [isOpen, open, close] = useFlag();

  return (
    <ClickAwayListener onClickAway={close}>
      <div>
        <Tooltip
          open={isOpen}
          placement="bottom"
          arrow={false}
          componentsProps={{ tooltip: { sx: { minWidth: 200, p: 1, willChange: 'transform' } } }}
          slotProps={{
            popper: {
              sx: (theme) => ({
                zIndex: theme.zIndex.modal + 1,
                '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                  marginTop: theme.spacing(0.25),
                },
                '&.MuiTooltip-popper[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                  marginBottom: theme.spacing(0.25),
                },
              }),
            },
          }}
          title={
            <Stack gap={0.25}>
              {actions.map((action) =>
                action.subActions?.length ? (
                  <FacilityActionsSubMenu key={action.label} action={action} onClose={close} />
                ) : (
                  <DropdownSelectRow
                    key={action.label}
                    isSelected={false}
                    onClick={() => {
                      if (!action.onClick) return;
                      action.onClick();
                      close();
                    }}
                  >
                    {action.label}
                  </DropdownSelectRow>
                ),
              )}
            </Stack>
          }
        >
          <IconButton inverse onClick={isOpen ? close : open}>
            <MoreIcon />
          </IconButton>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

interface FacilityActionsSubMenuProps {
  action: FacilityAction;
  onClose: () => void;
}

const FacilityActionsSubMenu: FC<FacilityActionsSubMenuProps> = ({ action, onClose }) => {
  const [isOpen, open, close] = useFlag();

  const { subActions, endIcon, label } = action;

  if (!subActions?.length) return null;

  return (
    <Tooltip
      open={isOpen}
      placement="right-start"
      arrow={false}
      PopperProps={{ disablePortal: true }}
      componentsProps={{
        tooltip: {
          sx: { minWidth: 200, maxHeight: 300, overflowY: 'scroll', p: 1, willChange: 'transform' },
        },
      }}
      slotProps={{
        popper: {
          sx: (theme) => ({
            '&.MuiTooltip-popper[data-popper-placement*="right"] .MuiTooltip-tooltip': {
              marginLeft: theme.spacing(1.25),
            },
            '&.MuiTooltip-popper[data-popper-placement*="left"] .MuiTooltip-tooltip': {
              marginRight: theme.spacing(1.25),
            },
            '&.MuiTooltip-popper[data-popper-placement*="start"] .MuiTooltip-tooltip': {
              marginTop: theme.spacing(-1),
            },
            '&.MuiTooltip-popper[data-popper-placement*="end"] .MuiTooltip-tooltip': {
              marginBottom: theme.spacing(-1),
            },
          }),
        },
      }}
      title={
        <Stack gap={0.25}>
          {subActions.map(({ label, startIcon, isSelected, onClick }) =>
            onClick ? (
              <Stack key={label} direction="row" alignItems="center" gap={0.25}>
                {startIcon}
                <DropdownSelectRow
                  isSelected={isSelected}
                  onClick={() => {
                    onClick();
                    onClose();
                  }}
                  sx={{ minWidth: 0, width: '100%' }}
                >
                  {label}
                </DropdownSelectRow>
              </Stack>
            ) : (
              <TypographyWithOverflowHint key={label} variant="h3" color="text.primary" p={0.5}>
                {label}
              </TypographyWithOverflowHint>
            ),
          )}
        </Stack>
      }
    >
      <div>
        <DropdownSelectRow isSelected={false} endIcon={endIcon} onClick={isOpen ? close : open}>
          {label}
        </DropdownSelectRow>
      </div>
    </Tooltip>
  );
};
